<script>
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";
import { injectScript } from "@/utils/scripts";

export default {
  props: {
    content: {
      type: String,
      required: false,
      default: ""
    },
    cmsContent: {
      type: Boolean,
      required: false,
      default: false
    },
    pushData: {
      type: Object,
      required: false,
      default: () => {}
    },
    context: {
      type: String,
      required: false,
      default: "zone de push"
    },
    promoPrinting: {
      type: Boolean,
      required: false
    }
  },
  emits: ["click", "navigate"],
  setup() {
    const { trackEvent } = useInteractionDoneTracking();
    return { trackEvent };
  },
  mounted() {
    this.$nextTick(this.addListeners);
    this.$nextTick(this.initVideoScript);
  },
  beforeUnmount() {
    this.removeListeners();
  },
  watch: {
    content: "contentUpdated"
  },
  data() {
    return {
      preventNavigation: false,
      links: []
    };
  },
  methods: {
    handleClick(e) {
      this.$emit("click");
      if (!this.cmsContent) {
        this.trackEvent({
          labelGA: trackingCodes.PROMOTIONCLICK,
          actionGA: trackingCodes.PROMOTIONCLICK,
          categoryGA: trackingCodes.CATEGORYGA.ecommerce,
          event: trackingCodes.PROMOTIONCLICK,
          ecommerce: {
            promotionClick: {
              promotions: [
                {
                  position: this.context,
                  id: this.pushData.id,
                  name: this.pushData.front_label
                    ? this.pushData.front_label
                    : this.pushData.code,
                  creative: this.pushData.nb_cols
                }
              ]
            }
          }
        });
      }
    },
    onIntersect(entries, observer) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }
        observer.unobserve(target);
        if (this.pushData) {
          this.trackEvent({
            labelGA: trackingCodes.PROMOIMPRESSION,
            actionGA: trackingCodes.PROMOIMPRESSION,
            categoryGA: trackingCodes.CATEGORYGA.ecommerce,
            event: trackingCodes.PROMOIMPRESSION,
            ecommerce: {
              promoView: {
                promotions: [
                  {
                    position: this.context,
                    id: this.pushData.id,
                    name: this.pushData.code,
                    creative: this.pushData.nb_cols
                  }
                ]
              }
            }
          });
        }
      });
    },
    navigate(event) {
      let target = event.target;
      let i = 0;
      // Go throught 5 parents max to find a tag
      while (
        i < 5 &&
        !(target instanceof HTMLAnchorElement) &&
        target.parentNode
      ) {
        target = target.parentNode;
        i++;
      }
      // If target is still not a link, ignore
      if (!(target instanceof HTMLAnchorElement)) {
        return;
      }
      const href = target.getAttribute("href");
      // Get link target, if local link, navigate with router link
      if (href && href[0] === "/" && !this.preventNavigation) {
        event.preventDefault();
        navigateToInternal(href);
        this.$emit("navigate");
        this.preventNavigation = true;
        setTimeout(() => {
          this.preventNavigation = false;
        }, 5000);
      }
    },
    contentUpdated() {
      this.removeListeners();
      this.$nextTick(() => {
        this.addListeners();
      });
    },
    addListeners() {
      this.links = this.$el.getElementsByTagName("a");
      for (let i = 0; i < this.links.length; i++) {
        this.links[i].addEventListener("click", this.navigate, false);
      }
    },
    async initVideoScript() {
      try {
        const videos = this.$el.querySelectorAll(".video-dam");
        if (videos.length > 0) {
          await injectScript(
            useRuntimeConfig().public.VIDEO_VIEWER_SCRIPT_URL,
            "video-viewer"
          );
        }
        // eslint-disable-next-line no-undef
        if (s7viewers) {
          for (let i = 0; i < videos.length; i++) {
            const asset = videos[i].getAttribute("asset");
            videos[i].id = getUuid(`video-${i}`);
            videos[i].style.display = "block";
            // eslint-disable-next-line no-undef
            new s7viewers.VideoViewer({
              containerId: videos[i].id,
              params: {
                asset,
                serverurl: useRuntimeConfig().public.VIDEO_VIEWER_SERVER_URL,
                contenturl: useRuntimeConfig().public.VIDEO_VIEWER_CONTENT_URL,
                config: useRuntimeConfig().public.VIDEO_VIEWER_CONFIG,
                emailurl: useRuntimeConfig().public.VIDEO_VIEWER_EMAIL_URL,
                videoserverurl:
                  useRuntimeConfig().public.VIDEO_VIEWER_VIDEO_SERVER_URL
              }
            }).init();
          }
        }
      } catch (error) {}
    },
    removeListeners() {
      if (this.links && this.links.length) {
        for (let i = 0; i < this.links.length; i++) {
          this.links[i].removeEventListener("click", this.navigate, false);
        }
      }
      this.links = [];
    }
  },
  computed: {
    pushContent() {
      return this.pushData ? this.pushData.html_content : null;
    }
  }
};
</script>

<template>
  <div
    v-if="promoPrinting"
    v-intersect="onIntersect"
    v-bind="$attrs"
    @click="handleClick"
    v-html="pushContent"
  />
  <div
    v-else
    v-bind="$attrs"
    v-html="content"
  />
</template>
